/* eslint-disable react/prop-types */
import React from 'react';
import { Field } from 'redux-form';
import FormFieldSelect from '../../../components/forms/FormFieldSelect';
import FormFieldSearch from '../../../components/forms/FormFieldSearch';
import {
  INTERVAL_YEAR,
  INTERVAL_MONTH,
  INTERVAL_WEEK,
  INTERVAL_DAY,
  INTERVAL_HOUR,
  intervalOptions,
  hoursOptions,
  daysOfWeekOptions,
  daysOfMonthOptions,
  monthsOptions,
  minutesOptions,
} from '../../../screens/ProjectsDashboard/components/DashboardPreset/components/helpers';
import {
  getMinutesScheduleError,
  hasAdjacentHours,
} from '../../ProjectsDashboard/components/DashboardPreset/components/scheduleMailConfigErrors';

/**
 * Common Mail Scheduler Fields Component
 * Used in both ManageEmailReports (with redux-form) and Custom Reports Mail Setup
 *
 * @param {Object} props
 * @param {string} props.member - Redux-form member path (e.g., "emailReports[0]") - if provided, uses Field components
 * @param {string} props.interval - Current interval value (year, month, week, day, hour) - used when not using redux-form
 * @param {Array} props.months - Selected months array - used when not using redux-form
 * @param {Array} props.daysOfMonth - Selected days of month array - used when not using redux-form
 * @param {Array} props.daysOfWeek - Selected days of week array - used when not using redux-form
 * @param {Array} props.hours - Selected hours array - used when not using redux-form
 * @param {Array} props.minutes - Selected minutes array - used when not using redux-form
 * @param {Function} props.onIntervalChange - Callback for interval change - used when not using redux-form
 * @param {Function} props.onMonthsChange - Callback for months change - used when not using redux-form
 * @param {Function} props.onDaysOfMonthChange - Callback for days of month change - used when not using redux-form
 * @param {Function} props.onDaysOfWeekChange - Callback for days of week change - used when not using redux-form
 * @param {Function} props.onHoursChange - Callback for hours change - used when not using redux-form
 * @param {Function} props.onMinutesChange - Callback for minutes change - used when not using redux-form
 * @param {boolean} props.showRecipients - Whether to show recipient fields (default: false)
 * @param {Function} props.getIntervalValue - Function to get current interval value when using redux-form (optional)
 * @param {Array} props.recipientTarget - Email recipients array - used when not using redux-form
 * @param {Array} props.recipientCcTarget - CC recipients array - used when not using redux-form
 * @param {Array} props.recipientBccTarget - BCC recipients array - used when not using redux-form
 * @param {string} props.timezone - Timezone value - used when not using redux-form (required if showTimezone is true)
 * @param {Function} props.onTimezoneChange - Callback for timezone change - used when not using redux-form (required if showTimezone is true)
 * @param {boolean} props.showTimezone - Whether to show timezone field (default: false - set to true to enable)
 * @param {boolean} props.disabled - Disable all scheduler fields (view-only)
 * @param {boolean} props.submitFailed - Show minutes validation after a save attempt
 */
const MailSchedulerFields = ({
  member,
  interval,
  months = [],
  daysOfMonth = [],
  daysOfWeek = [],
  hours = [],
  minutes = [],
  onIntervalChange,
  onMonthsChange,
  onDaysOfMonthChange,
  onDaysOfWeekChange,
  onHoursChange,
  onMinutesChange,
  getIntervalValue,
  timezone,
  onTimezoneChange,
  showTimezone = false,
  disabled = false,
  submitFailed = false,
}) => {
  // Determine if we're using redux-form (member is provided)
  const useReduxForm = !!member;

  // Get interval value - either from props or from redux-form
  const currentInterval = useReduxForm
    ? getIntervalValue
      ? getIntervalValue()
      : null
    : interval;
  const wrapAroundMinutes =
    currentInterval === INTERVAL_HOUR || hasAdjacentHours(hours);
  const minutesError = getMinutesScheduleError(minutes, {
    wrapAround: wrapAroundMinutes,
  });

  // Render field using redux-form Field or regular component
  const renderField = (fieldName, component, props) => {
    const fieldProps = disabled ? { ...props, disabled: true } : props;
    if (useReduxForm) {
      return (
        <Field
          component={component}
          name={`${member}.${fieldName}`}
          {...fieldProps}
        />
      );
    }
    return React.createElement(component, fieldProps);
  };

  return (
    <>
      {renderField('interval', FormFieldSelect, {
        label: 'Repeat Frequency',
        options: intervalOptions,
        required: true,
        placeholder: 'Select interval',
        ...(useReduxForm
          ? {}
          : {
              input: {
                value: interval || null,
                onChange: onIntervalChange,
              },
              meta: {
                submitFailed: false,
                error: null,
              },
            }),
      })}

      {currentInterval === INTERVAL_YEAR &&
        renderField('months', FormFieldSearch, {
          label: 'Month(s)',
          options: monthsOptions,
          mode: 'multiple',
          showSearch: true,
          required: true,
          ...(useReduxForm
            ? {}
            : {
                input: {
                  value: months || [],
                  onChange: onMonthsChange,
                },
                meta: { submitFailed: false, error: null },
              }),
        })}

      {(currentInterval === INTERVAL_YEAR ||
        currentInterval === INTERVAL_MONTH) &&
        renderField('daysOfMonth', FormFieldSearch, {
          label: 'Day(s) of Month',
          options: daysOfMonthOptions,
          mode: 'multiple',
          showSearch: true,
          required: true,
          ...(useReduxForm
            ? {}
            : {
                input: {
                  value: daysOfMonth || [],
                  onChange: onDaysOfMonthChange,
                },
                meta: { submitFailed: false, error: null },
              }),
        })}

      {(currentInterval === INTERVAL_YEAR ||
        currentInterval === INTERVAL_MONTH ||
        currentInterval === INTERVAL_WEEK) &&
        renderField('daysOfWeek', FormFieldSearch, {
          label: 'Day(s) of Week',
          options: daysOfWeekOptions,
          mode: 'multiple',
          showSearch: true,
          required: true,
          ...(useReduxForm
            ? {}
            : {
                input: {
                  value: daysOfWeek || [],
                  onChange: onDaysOfWeekChange,
                },
                meta: { submitFailed: false, error: null },
              }),
        })}

      {(currentInterval === INTERVAL_YEAR ||
        currentInterval === INTERVAL_MONTH ||
        currentInterval === INTERVAL_WEEK ||
        currentInterval === INTERVAL_DAY) &&
        renderField('hours', FormFieldSearch, {
          label: 'Hour(s) of Day',
          options: hoursOptions,
          mode: 'multiple',
          showSearch: true,
          required: true,
          ...(useReduxForm
            ? {}
            : {
                input: {
                  value: hours || [],
                  onChange: onHoursChange,
                },
                meta: { submitFailed: false, error: null },
              }),
        })}

      {(currentInterval === INTERVAL_YEAR ||
        currentInterval === INTERVAL_MONTH ||
        currentInterval === INTERVAL_WEEK ||
        currentInterval === INTERVAL_DAY ||
        currentInterval === INTERVAL_HOUR) &&
        renderField('minutes', FormFieldSearch, {
          label: 'Minutes',
          tooltip:
            'Use 5-minute steps (0, 5, 10, …). Selections must be at least 15 minutes apart, e.g. 5, 20, 35.',
          options: minutesOptions,
          mode: 'multiple',
          showSearch: true,
          required: true,
          ...(useReduxForm
            ? {}
            : {
                input: {
                  value: minutes || [],
                  onChange: onMinutesChange,
                },
                meta: {
                  touched: submitFailed || !!minutesError,
                  submitFailed,
                  error: minutesError || null,
                },
              }),
        })}

      {showTimezone &&
        renderField('timezone', FormFieldSearch, {
          'data-testid': useReduxForm ? 'field-timezone' : undefined,
          label: useReduxForm ? 'Timezone' : 'Timezone',
          valueSetId: 'zedoc/timezones@1.0.0',
          required: true,
          ...(useReduxForm
            ? {}
            : {
                input: {
                  value: timezone || null,
                  onChange: onTimezoneChange,
                },
                meta: { submitFailed: false, error: null },
              }),
        })}
    </>
  );
};

export default MailSchedulerFields;
